import { VersionInfo, AppVersion } from '../types/version';

// Import version data (will be generated during build)
let versionData: VersionInfo;

try {
  versionData = require('../version.json');
} catch (error) {
  // Fallback version data if version.json doesn't exist
  const now = new Date();
  versionData = {
    version: '0.0.0.dev',
    timestamp: now.toISOString(),
    date: now.toLocaleDateString('en-IN'),
    time: now.toLocaleTimeString('en-IN'),
    buildNumber: Math.floor(now.getTime() / 1000),
    gitBranch: 'development',
    environment: 'development'
  };
}

/**
 * Version utility class for the application
 */
export class Version implements AppVersion {
  private static instance: Version;
  private versionInfo: VersionInfo;

  constructor() {
    this.versionInfo = versionData;
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): Version {
    if (!Version.instance) {
      Version.instance = new Version();
    }
    return Version.instance;
  }

  /**
   * Get the version string
   */
  public getVersion(): string {
    return this.versionInfo.version;
  }

  /**
   * Get full version information
   */
  public getFullVersionInfo(): VersionInfo {
    return { ...this.versionInfo };
  }

  /**
   * Get build date in readable format
   */
  public getBuildDate(): string {
    return this.versionInfo.date;
  }

  /**
   * Get build time in readable format
   */
  public getBuildTime(): string {
    return this.versionInfo.time;
  }

  /**
   * Check if running in production
   */
  public isProduction(): boolean {
    return this.versionInfo.environment === 'production';
  }

  /**
   * Get formatted version string for display
   */
  public getDisplayVersion(): string {
    const { version, date, time } = this.versionInfo;
    return `v${version} (${date} at ${time})`;
  }

  /**
   * Get short version for headers/footers
   */
  public getShortVersion(): string {
    return `v${this.versionInfo.version}`;
  }

  /**
   * Get build number
   */
  public getBuildNumber(): number {
    return this.versionInfo.buildNumber;
  }

  /**
   * Get git branch
   */
  public getGitBranch(): string {
    return this.versionInfo.gitBranch;
  }

  /**
   * Get environment
   */
  public getEnvironment(): string {
    return this.versionInfo.environment;
  }

  /**
   * Get version for API headers
   */
  public getAPIVersion(): string {
    return this.versionInfo.version;
  }

  /**
   * Format version for console logging
   */
  public getConsoleVersion(): string {
    const { version, date, time, environment, gitBranch } = this.versionInfo;
    return `GaadiKharcha v${version} | ${date} ${time} | ${environment} | ${gitBranch}`;
  }
}

// Export singleton instance
export const appVersion = Version.getInstance();

// Export helper functions for convenience
export const getVersion = () => appVersion.getVersion();
export const getDisplayVersion = () => appVersion.getDisplayVersion();
export const getShortVersion = () => appVersion.getShortVersion();
export const getBuildDate = () => appVersion.getBuildDate();
export const getBuildTime = () => appVersion.getBuildTime();
export const isProduction = () => appVersion.isProduction();
export const getFullVersionInfo = () => appVersion.getFullVersionInfo();
